import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './FeaturedSubscriptionForm.css';

const FeaturedSubscriptionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'Hotel',
    description: '',
    duration: '',
    durationUnit: 'days',
    price: '',
    currency: 'INR',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchSubscription();
    }
  }, [id]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/admin/featured-subscriptions/${id}`);
      
      if (response.data && response.data.success) {
        const subscription = response.data.data;
        if (subscription) {
          setFormData({
            name: subscription.name || '',
            type: subscription.type || 'Hotel',
            description: subscription.description || '',
            duration: subscription.duration || '',
            durationUnit: subscription.durationUnit || 'days',
            price: subscription.price || '',
            currency: subscription.currency || 'INR',
            status: subscription.status || 'active'
          });
        } else {
          setError('Subscription data not found in response');
        }
      } else {
        setError('Invalid response from server');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch subscription';
      setError(`Error: ${errorMessage}${error.response?.status ? ` (Status: ${error.response.status})` : ''}`);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    if (!formData.name || !formData.type || !formData.duration || !formData.price) {
      setError('Please fill in all required fields (name, type, duration, price)');
      setSaving(false);
      return;
    }

    try {
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/featured-subscriptions/${id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/admin/featured-subscriptions`, formData);
      }
      navigate('/sy-admin/featured-subscriptions');
    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} subscription`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="featured-subscription-form-container">
          <div className="featured-subscription-form-header">
            <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="featured-subscription-form-card">
            <div className="form-section">
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '32px', borderRadius: '8px', marginBottom: '24px' }}></div>
              
              {/* Name Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
              </div>

              {/* Description Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '80px', borderRadius: '8px' }}></div>
              </div>

              {/* Duration Row */}
              <div className="form-row">
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>

              {/* Price Row */}
              <div className="form-row">
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>

              {/* Status Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '200px', height: '44px', borderRadius: '8px' }}></div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <div className="skeleton skeleton-button" style={{ width: '200px', height: '44px', borderRadius: '8px' }}></div>
              <div className="skeleton skeleton-button" style={{ width: '100px', height: '44px', borderRadius: '8px' }}></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="featured-subscription-form-container">
        <div className="featured-subscription-form-header">
          <h1>{isEdit ? 'Edit Featured Subscription' : 'Create New Featured Subscription'}</h1>
          <button
            onClick={() => navigate('/sy-admin/featured-subscriptions')}
            className="btn btn-secondary"
          >
            ← Back to Subscriptions
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="featured-subscription-form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h2>Subscription Details</h2>
              
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., 30 Days Featured"
                />
              </div>

              <div className="form-group">
                <label htmlFor="type">Type *</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="Hotel">Hotel</option>
                  <option value="SPA">SPA</option>
                </select>
                <span className="form-hint">Assign this plan only to Hotels or only to Spas</span>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Subscription description"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="duration">Duration *</label>
                  <input
                    type="number"
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                    min="1"
                    placeholder="e.g., 30"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="durationUnit">Duration Unit *</label>
                  <select
                    id="durationUnit"
                    name="durationUnit"
                    value={formData.durationUnit}
                    onChange={handleChange}
                    required
                  >
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price">Price *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="currency">Currency</label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (isEdit ? 'Update Subscription' : 'Create Subscription')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/sy-admin/featured-subscriptions')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default FeaturedSubscriptionForm;
